import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { CheckCircle, Clock, Loader2 } from "lucide-react";
import { format } from "date-fns";

interface InstructorSelfAttendanceProps {
  userId: string;
}

export const InstructorSelfAttendance = ({ userId }: InstructorSelfAttendanceProps) => {
  const [isMarked, setIsMarked] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isMarking, setIsMarking] = useState(false);
  const [instructorId, setInstructorId] = useState<string | null>(null);

  useEffect(() => {
    const checkAttendance = async () => {
      setIsLoading(true);
      const today = format(new Date(), "yyyy-MM-dd");

      try {
        // Get instructor record for this user
        const { data: instructor, error: instructorError } = await supabase
          .from("instructors")
          .select("id")
          .eq("user_id", userId)
          .single();

        if (instructorError || !instructor) {
          setIsLoading(false);
          return;
        }

        setInstructorId(instructor.id);

        // Check if attendance is already marked for today
        const { data: attendance, error: attendanceError } = await supabase
          .from("instructor_attendance")
          .select("id")
          .eq("instructor_id", instructor.id)
          .eq("date", today)
          .maybeSingle();

        if (!attendanceError && attendance) {
          setIsMarked(true);
        }
      } catch (error) {
        console.error("Error checking attendance:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (userId) {
      checkAttendance();
    }
  }, [userId]);

  const handleMarkAttendance = async () => {
    if (!instructorId) return;

    setIsMarking(true);
    const today = format(new Date(), "yyyy-MM-dd");

    try {
      const { error } = await supabase.from("instructor_attendance").insert({
        instructor_id: instructorId,
        date: today,
        status: "Present",
      });

      if (error) throw error;

      setIsMarked(true);
      toast.success("Attendance marked successfully!");
    } catch (error: any) {
      console.error("Error marking attendance:", error);
      if (error.code === "23505") {
        setIsMarked(true);
        toast.info("Attendance already marked for today");
      } else {
        toast.error("Failed to mark attendance");
      }
    } finally {
      setIsMarking(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-6 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!instructorId) {
    return null;
  }

  return (
    <Card className={isMarked ? "border-green-500/50 bg-green-500/5" : "border-primary/50"}>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          {isMarked ? (
            <CheckCircle className="h-5 w-5 text-green-500" />
          ) : (
            <Clock className="h-5 w-5 text-primary" />
          )}
          <CardTitle className="text-lg">Today's Attendance</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-4">
          {format(new Date(), "EEEE, MMMM d, yyyy")}
        </p>
        
        {isMarked ? (
          <div className="flex items-center gap-2 text-green-600">
            <CheckCircle className="h-5 w-5" />
            <span className="font-medium">Present - Attendance Marked</span>
          </div>
        ) : (
          <Button
            onClick={handleMarkAttendance}
            disabled={isMarking}
            className="w-full"
          >
            {isMarking ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Marking...
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                Mark Present
              </>
            )}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
